import { ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { MoreHorizontal, Edit, Trash2, Shield, Eye } from 'lucide-react'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useState } from 'react'
import type { Role } from '@/types/entities'
import { getResponsiveColumnSize } from '@/lib/utils/responsive-columns'

interface RoleActionsProps {
    role: Role
    onEdit?: (role: Role) => void
    onDelete?: (role: Role) => void
    onView?: (role: Role) => void
    t?: (key: string, options?: any) => string
    isSystemRole: boolean
}

function RoleActions({ role, onEdit, onDelete, onView, t, isSystemRole }: RoleActionsProps) {
    const [open, setOpen] = useState(false)

    return (
        <div
            className="flex justify-center"
            onClick={(e) => {
                // Prevent table row click handlers
                e.stopPropagation()
            }}
            onMouseDown={(e) => {
                // Prevent table focus changes
                e.stopPropagation()
            }}
            onMouseUp={(e) => {
                e.stopPropagation()
            }}
            onPointerDown={(e) => {
                e.stopPropagation()
            }}
            onPointerUp={(e) => {
                e.stopPropagation()
            }}
        >
            <DropdownMenu open={open} onOpenChange={setOpen} modal={true}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            setOpen(true)
                        }}
                        onMouseDown={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                        }}
                        onMouseUp={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                        }}
                    >
                        <span className="sr-only">{t ? t('roles.openMenu') : 'Open menu'}</span>
                        <MoreHorizontal className="h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="end"
                    side="bottom"
                    sideOffset={5}
                    className="w-56"
                    onCloseAutoFocus={(e) => {
                        // Prevent focus from jumping back into table
                        e.preventDefault()
                    }}
                >
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.stopPropagation()
                            onView?.(role)
                            setOpen(false)
                        }}
                    >
                        <Eye className="mr-2 h-4 w-4" />
                        {t ? t('roles.view') : 'View'}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.stopPropagation()
                            onEdit?.(role)
                            setOpen(false)
                        }}
                        disabled={isSystemRole}
                    >
                        <Edit className="mr-2 h-4 w-4" />
                        {t ? t('roles.edit') : 'Edit'}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={(e) => {
                            e.stopPropagation()
                            onDelete?.(role)
                            setOpen(false)
                        }}
                        disabled={isSystemRole}
                    >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {t ? t('roles.delete') : 'Delete'}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    )
}

interface RoleColumnsProps {
    onEdit?: (role: Role) => void
    onDelete?: (role: Role) => void
    onView?: (role: Role) => void
    t?: (key: string, options?: any) => string
}

export function createRoleColumns({ onEdit, onDelete, onView, t }: RoleColumnsProps = {}): ColumnDef<Role>[] {
    // Calculate responsive column sizes
    const columnSizes = {
        name: getResponsiveColumnSize(200),
        description: getResponsiveColumnSize(300),
        permissions: getResponsiveColumnSize(130),
        actions: 80  // Fixed width
    }

    return [
        {
            accessorKey: 'name',
            header: t ? t('roles.name') : 'Name',
            size: columnSizes.name,
            minSize: 150,
            maxSize: 320,
            cell: ({ row }) => {
                const role = row.original
                // Use the translated name from the row data (which comes from useDatabaseTranslationRecords)
                // The name field is already translated if translation is enabled
                const displayName = role.name || ''
                return (
                    <div className="flex items-center space-x-2">
                        <Shield className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-sm">{displayName}</span>
                        {(role.name === 'admin' || displayName.toLowerCase() === 'admin' || displayName.toLowerCase().includes('admin')) && (
                            <Badge variant="destructive" className="text-xs">
                                {t ? t('roles.system') : 'System'}
                            </Badge>
                        )}
                    </div>
                )
            },
        },
        {
            accessorKey: 'description',
            header: t ? t('roles.descriptionColumn') : 'Description',
            size: columnSizes.description,
            minSize: 250,
            maxSize: 500,
            cell: ({ row }) => {
                const role = row.original
                return (
                    <span className="text-sm text-muted-foreground">
                        {role.description || (t ? t('roles.noDescription') : 'No description')}
                    </span>
                )
            },
        },
        {
            accessorKey: 'permissions',
            header: t ? t('roles.permissions') : 'Permissions',
            size: columnSizes.permissions,
            minSize: 110,
            maxSize: 200,
            cell: ({ row }) => {
                const role = row.original
                const permissionCount = role.permissions?.length || 0
                return (
                    <Badge variant="secondary">
                        {t ? t('roles.permissionCount', { count: permissionCount }) : `${permissionCount} permission${permissionCount !== 1 ? 's' : ''}`}
                    </Badge>
                )
            },
        },
        {
            id: 'actions',
            size: columnSizes.actions,
            minSize: 80,
            maxSize: 80,
            header: () => (
                <div className="text-center w-full">
                    {t ? t('common.actions') : 'Actions'}
                </div>
            ),
            cell: ({ row, table }) => {
                const role = row.original
                // Get the original role name from the original data (before translation)
                // Check if we have access to the original data via table options or metadata
                const originalName = (role as any)._originalName || role.name
                // For system role check, compare against original name (before translation)
                // The translated name might be in a different language, so we need the original
                const isSystemRole = role.hasOwnProperty('isSystem')
                    ? (role as any).isSystem
                    : originalName === 'admin' || originalName === 'user' || originalName === 'viewer'

                return (
                    <RoleActions
                        role={role}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onView={onView}
                        t={t}
                        isSystemRole={isSystemRole}
                    />
                )
            },
        },
    ]
}
